import React, {useCallback, useMemo, useState} from 'react';
import {Button, Checkbox, MenuItem, Popover} from '@blueprintjs/core';
import styled from 'styled-components';

import {StyledFormGroup} from './shared';


// --- Tree building from delimiter-separated flat strings ---

const buildTree = (items, delimiter) => {
    const nodeMap = new Map();
    const roots = [];

    const ensureNode = (value, segments) => {
        if (nodeMap.has(value)) return nodeMap.get(value);
        const node = {
            value,
            label: segments[segments.length - 1],
            children: [],
            selectable: false,
        };
        nodeMap.set(value, node);

        if (segments.length === 1) {
            roots.push(node);
        } else {
            const parentValue = segments.slice(0, -1).join(delimiter);
            const parent = ensureNode(parentValue, segments.slice(0, -1));
            parent.children.push(node);
        }

        return node;
    };

    const normalized = items.map(item =>
        typeof item === 'string' ? {value: item, label: item} : item
    );

    for (const item of normalized) {
        const segments = item.value.split(delimiter);
        const node = ensureNode(item.value, segments);
        node.selectable = true;
        if (item.label !== item.value) node.label = item.label;
    }

    return roots;
};

const collectSelectableValues = (nodes) => {
    const values = [];
    const walk = (ns) => {
        for (const n of ns) {
            if (n.selectable) values.push(n.value);
            if (n.children.length > 0) walk(n.children);
        }
    };
    walk(nodes);
    return values;
};


// --- Styled components ---

const SelectButton = styled(Button)`
    &[class*="bp"][class*="-button"] {
        border-radius: var(--sf-border-radius) !important;
        width: fit-content;
        justify-content: space-between;
        background: transparent !important;
        box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.2) !important;
    }
`;

const PanelContainer = styled.div`
    display: flex;
`;

const Panel = styled.div`
    min-width: 150px;
    max-height: 320px;
    display: flex;
    flex-direction: column;

    &:not(:first-child) {
        border-left: 1px solid rgba(17, 20, 24, 0.15);
    }
`;

const PanelHeader = styled.div`
    padding: 6px 10px;
    font-weight: 600;
    font-size: 11px;
    color: #5c7080;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(17, 20, 24, 0.15);
    white-space: nowrap;
`;

const ActionBar = styled.div`
    display: flex;
    gap: 4px;
    padding: 4px 5px;
    border-bottom: 1px solid rgba(17, 20, 24, 0.15);
    & > * { flex: 1; }
`;

const PanelBody = styled.div`
    overflow-y: auto;
    flex: 1;
`;

const ItemCheckbox = styled(Checkbox)`
    margin-bottom: 0;
    pointer-events: none;
`;

const IndentedItem = styled(MenuItem)`
    &[class*="bp"][class*="-menu-item"] {
        padding-left: ${p => 7 + (p.$depth || 0) * 14}px !important;
    }
`;

const GroupHeader = styled.div`
    padding: 6px 7px 2px ${p => 7 + (p.$depth || 0) * 14}px;
    font-weight: 600;
    font-size: 11px;
    color: #8a9ba8;
`;


// --- Recursive tree renderer ---

const TreeNodes = ({nodes, selected, onItemClick, multi, depth}) => (
    <>
        {nodes.map(node => (
            <React.Fragment key={node.value}>
                {node.selectable ? (
                    <IndentedItem
                        text={multi
                            ? <ItemCheckbox checked={selected.includes(node.value)} label={node.label} />
                            : node.label
                        }
                        $depth={depth}
                        active={!multi && selected === node.value}
                        onClick={() => onItemClick(node.value)}
                        shouldDismissPopover={false}
                    />
                ) : (
                    <GroupHeader $depth={depth}>{node.label}</GroupHeader>
                )}
                {node.children.length > 0 && (
                    <TreeNodes
                        nodes={node.children}
                        selected={selected}
                        onItemClick={onItemClick}
                        multi={multi}
                        depth={depth + 1}
                    />
                )}
            </React.Fragment>
        ))}
    </>
);


// --- Flat item renderer ---

const FlatItems = ({items, selected, onItemClick, multi}) => (
    <>
        {items.map(item => (
            <MenuItem
                key={item.value}
                text={multi
                    ? <ItemCheckbox checked={selected.includes(item.value)} label={item.label} />
                    : item.label
                }
                active={!multi && selected === item.value}
                onClick={() => onItemClick(item.value)}
                shouldDismissPopover={false}
            />
        ))}
    </>
);


// --- Helpers ---

const normalizeItems = (items) =>
    items.map(item => typeof item === 'string' ? {value: item, label: item} : item);

/**
 * Resolve items for a level.
 *
 * Uses `activeMap` — a single "focused" value per ancestor level — to look up
 * `itemsFor` entries. When `dependsOn` is set, only that specific ancestor is
 * checked; otherwise ancestors are searched from immediate parent backwards.
 */
const getLevelItems = (level, levelIndex, levels, activeMap) => {
    if (level.items) return level.items;
    if (level.itemsFor) {
        if (level.dependsOn) {
            const val = activeMap[level.dependsOn];
            if (val && level.itemsFor[val]) return normalizeItems(level.itemsFor[val]);
            return [];
        }
        for (let i = levelIndex - 1; i >= 0; i--) {
            const val = activeMap[levels[i].key];
            if (val && level.itemsFor[val]) return normalizeItems(level.itemsFor[val]);
        }
        return [];
    }
    return [];
};

const getSingleDisplayText = (levels, value) => {
    const parts = [];
    for (const level of levels) {
        const val = value[level.key];
        if (val == null) break;

        if (level.items) {
            const item = level.items.find(i => i.value === val);
            parts.push(item?.label ?? val);
        } else if (level.delimiter) {
            parts.push(...val.split(level.delimiter));
        } else {
            parts.push(val);
        }
    }
    return parts.length > 0 ? parts.join(' \u203a ') : null;
};

const getMultiDisplayText = (levels, value) => {
    const parts = [];
    for (const level of levels) {
        // Non-selectable levels store a string — show as prefix
        if (level.selectable === false) {
            const val = value[level.key];
            if (val) {
                const item = level.items?.find(i => i.value === val);
                parts.push(item?.label ?? val);
            }
            continue;
        }

        const selected = value[level.key] || [];
        if (selected.length === 0) continue;

        if (selected.length === 1) {
            const val = selected[0];
            if (level.items) {
                const item = level.items.find(i => i.value === val);
                parts.push(item?.label ?? val);
            } else if (level.delimiter) {
                const segments = val.split(level.delimiter);
                parts.push(segments[segments.length - 1]);
            } else {
                parts.push(val);
            }
        } else {
            parts.push(`${selected.length} ${level.label}`);
        }
    }
    return parts.length > 0 ? parts.join(' \u00b7 ') : null;
};


// --- Main component ---

const CascadingSelectField = ({label, value, disabled, error, levels, multi, onChange}) => {
    const currentValue = value || {};
    const intent = error ? 'danger' : 'none';

    // Internal "focused" item for multi-selectable levels — determines what
    // child panels show (separate from the checked values).
    const [focused, setFocused] = useState(() => {
        const f = {};
        for (const level of levels) {
            if (multi && level.selectable !== false) {
                const val = (value || {})[level.key];
                if (Array.isArray(val) && val.length > 0) f[level.key] = val[0];
            }
        }
        return f;
    });

    // Active map: one focused value per level, used by getLevelItems.
    //   selectable:false  → value[key] (string stored in data)
    //   multi selectable  → focused[key] (internal state, last clicked)
    //   single selectable → value[key] (string stored in data)
    const activeMap = useMemo(() => {
        const map = {};
        for (const level of levels) {
            if (level.selectable === false) {
                map[level.key] = currentValue[level.key];
            } else if (multi) {
                map[level.key] = focused[level.key];
            } else {
                map[level.key] = currentValue[level.key];
            }
        }
        return map;
    }, [levels, currentValue, focused, multi]);

    // Non-selectable: store string value (tab switch, no child clearing)
    const handleActivate = useCallback((levelKey, itemValue) => {
        onChange({...currentValue, [levelKey]: itemValue});
    }, [currentValue, onChange]);

    // Single-select: replace value, clear children
    const handleSelect = useCallback((levelIndex, selectedValue) => {
        const newValue = {};
        for (let i = 0; i < levels.length; i++) {
            if (i < levelIndex) {
                newValue[levels[i].key] = currentValue[levels[i].key];
            } else if (i === levelIndex) {
                newValue[levels[i].key] = selectedValue;
            }
        }
        onChange(newValue);
    }, [levels, currentValue, onChange]);

    // Multi-select: toggle item in/out of array + track focused
    const handleToggle = useCallback((levelKey, itemValue) => {
        const current = currentValue[levelKey] || [];
        const updated = current.includes(itemValue)
            ? current.filter(v => v !== itemValue)
            : [...current, itemValue];
        onChange({...currentValue, [levelKey]: updated});
        setFocused(prev => ({...prev, [levelKey]: itemValue}));
    }, [currentValue, onChange]);

    const handleSelectAll = useCallback((levelKey, allValues) => {
        onChange({...currentValue, [levelKey]: allValues});
    }, [currentValue, onChange]);

    const handleClear = useCallback((levelKey) => {
        onChange({...currentValue, [levelKey]: []});
    }, [currentValue, onChange]);

    const displayText = useMemo(
        () => multi
            ? getMultiDisplayText(levels, currentValue)
            : getSingleDisplayText(levels, currentValue),
        [levels, currentValue, multi],
    );

    const panels = useMemo(() => {
        return levels.map((level, i) => {
            const items = getLevelItems(level, i, levels, activeMap);
            if (items.length === 0 && i > 0) return null;

            const levelMulti = multi && level.selectable !== false;
            const tree = level.delimiter ? buildTree(items, level.delimiter) : null;
            const selected = levelMulti
                ? (currentValue[level.key] || [])
                : currentValue[level.key];
            const allValues = tree
                ? collectSelectableValues(tree)
                : items.map(item => item.value);

            return {level, items, selected, tree, allValues, index: i, levelMulti};
        }).filter(Boolean);
    }, [levels, currentValue, activeMap, multi]);

    return (
        <StyledFormGroup label={label} helperText={error?.message} intent={intent}>
            <Popover
                disabled={disabled}
                minimal
                placement="bottom-start"
                content={
                    <PanelContainer>
                        {panels.map(({level, items, selected, tree, allValues, index, levelMulti}) => {
                            const onItemClick = levelMulti
                                ? (val) => handleToggle(level.key, val)
                                : level.selectable === false
                                    ? (val) => handleActivate(level.key, val)
                                    : (val) => handleSelect(index, val);

                            return (
                                <Panel key={level.key}>
                                    <PanelHeader>{level.label}</PanelHeader>
                                    {levelMulti && (
                                        <ActionBar>
                                            <Button small outlined text="All" onClick={() => handleSelectAll(level.key, allValues)} />
                                            <Button small outlined text="Clear" onClick={() => handleClear(level.key)} />
                                        </ActionBar>
                                    )}
                                    <PanelBody>
                                        {tree ? (
                                            <TreeNodes
                                                nodes={tree}
                                                selected={selected}
                                                onItemClick={onItemClick}
                                                multi={levelMulti}
                                                depth={0}
                                            />
                                        ) : (
                                            <FlatItems
                                                items={items}
                                                selected={selected}
                                                onItemClick={onItemClick}
                                                multi={levelMulti}
                                            />
                                        )}
                                    </PanelBody>
                                </Panel>
                            );
                        })}
                    </PanelContainer>
                }
            >
                <SelectButton
                    text={displayText || 'Select\u2026'}
                    rightIcon="chevron-down"
                    disabled={disabled}
                />
            </Popover>
        </StyledFormGroup>
    );
};

export {CascadingSelectField};
